import React, { useEffect, useMemo, useState } from 'react';
import { X, Flag } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

interface ReportDialogProps {
  isOpen: boolean;
  title: string;
  itemDescription?: string;
  onClose: () => void;
  onSubmit: (payload: { reason: string; description?: string }) => Promise<void>;
}

const ReportDialog: React.FC<ReportDialogProps> = ({ isOpen, title, itemDescription, onClose, onSubmit }) => {
  const { select } = useLanguage();
  const reasonOptions = useMemo(
    () => [
      { value: 'inappropriate', label: select({ es: 'Contenido inapropiado', en: 'Inappropriate content' }) },
      { value: 'spam', label: select({ es: 'Spam o publicidad no deseada', en: 'Spam or unwanted advertising' }) },
      { value: 'impersonation', label: select({ es: 'Suplantación o identidad falsa', en: 'Impersonation or fake identity' }) },
      { value: 'hate', label: select({ es: 'Incitación al odio o violencia', en: 'Hate speech or violence' }) },
      { value: 'copyright', label: select({ es: 'Infracción de derechos de autor', en: 'Copyright infringement' }) },
      { value: 'other', label: select({ es: 'Otro', en: 'Other' }) },
    ],
    [select]
  );
  const [selectedReason, setSelectedReason] = useState<string>(reasonOptions[0].value);
  const [customReason, setCustomReason] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setSelectedReason(reasonOptions[0].value);
      setCustomReason('');
      setDescription('');
      setSubmitting(false);
      setError('');
    }
  }, [isOpen, reasonOptions]);

  if (!isOpen) {
    return null;
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const isOther = selectedReason === 'other';
    const selectedOption = reasonOptions.find((option) => option.value === selectedReason);
    const reasonLabel = selectedOption?.label || selectedReason;
    const reasonToSend = isOther
      ? customReason.trim() || reasonLabel
      : reasonLabel;

    if (isOther && !customReason.trim()) {
      setError(select({ es: 'Describe el motivo del reporte.', en: 'Please describe the reason for the report.' }));
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      await onSubmit({
        reason: reasonToSend,
        description: description.trim() || undefined,
      });
      onClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : undefined;
      setError(msg || select({ es: 'No se pudo enviar el reporte. Intenta de nuevo.', en: 'Could not send the report. Please try again.' }));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="bg-white w-full max-w-md rounded-2xl shadow-xl border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-full bg-red-100 text-red-600 flex items-center justify-center">
              <Flag size={18} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
              {itemDescription && (
                <p className="text-sm text-gray-500 line-clamp-1">{itemDescription}</p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 text-gray-500"
            aria-label={select({ es: 'Cerrar', en: 'Close' })}
          >
            <X size={18} />
          </button>
        </div>

        <form className="px-6 py-5 space-y-4" onSubmit={handleSubmit}>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{select({ es: 'Motivo del reporte', en: 'Reason for report' })}</label>
            <select
              value={selectedReason}
              onChange={(event) => setSelectedReason(event.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
            >
              {reasonOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {selectedReason === 'other' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{select({ es: 'Describe el motivo', en: 'Describe the reason' })}</label>
              <input
                type="text"
                value={customReason}
                onChange={(event) => setCustomReason(event.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                placeholder={select({ es: 'Escribe el motivo del reporte', en: 'Enter the reason for the report' })}
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {select({ es: 'Información adicional (opcional)', en: 'Additional information (optional)' })}
            </label>
            <textarea
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              rows={4}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm resize-none"
              placeholder={select({ es: 'Explica qué sucede o añade enlaces relevantes', en: 'Explain what happened or add relevant links' })}
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800"
            >
              {select({ es: 'Cancelar', en: 'Cancel' })}
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1 disabled:opacity-70"
            >
              {submitting && (
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
              )}
              {select({ es: 'Enviar reporte', en: 'Submit report' })}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReportDialog;
